/**
 * JWT business plane guard — auth required · External · legacy JWT 차단 (D2 · TP-G26)
 *
 * Public members (`entityType=user`) remain admissible under
 * `canAccessBusinessPlane` global semantics, but must not render the internal
 * Business shell — redirect to the canonical public-member destination `/my`.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  canAccessBusinessPlane,
  PORTAL_ENTRY_PATH,
  resolvePostLoginPath,
  type SiteEntityType,
} from '@shared/tenant-host';
import { clearStoredAuth, getCurrentTokenPayload, isValidToken } from '@frontend/utils/jwtUtils';

export const BusinessPlaneGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  if (!isValidToken()) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname, requiresAuth: true, businessPlane: true }}
      />
    );
  }

  const payload = getCurrentTokenPayload();
  const entityType = (payload?.entityType ?? null) as SiteEntityType;

  if (!canAccessBusinessPlane(entityType)) {
    if (entityType === null) {
      clearStoredAuth();
      return (
        <Navigate to="/login" replace state={{ legacyJwtRelogin: true, from: location.pathname }} />
      );
    }

    return (
      <Navigate
        to={PORTAL_ENTRY_PATH}
        replace
        state={{ businessPlaneBlocked: true, from: location.pathname }}
      />
    );
  }

  if (entityType === 'user') {
    return (
      <Navigate
        to={resolvePostLoginPath('user')}
        replace
        state={{ businessShellBlockedForPublicMember: true, from: location.pathname }}
      />
    );
  }

  return <>{children}</>;
};

export default BusinessPlaneGuard;
